import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';

// Helper function to get CSRF token
const getCSRFToken = (): string => {
  if (typeof document !== 'undefined') {
    return (
      document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute('content') || ''
    );
  }
  return '';
};

/**
 * Custom axios instance for legacy AnyRoad API
 * Automatically includes CSRF token in all requests
 * Similar pattern to the generated client in packages/libs/api
 */
export const legacyApiInstance = <T>(
  config: AxiosRequestConfig,
  options?: AxiosRequestConfig
): Promise<T> => {
  const axiosInstance = axios.create({
    baseURL: '', // Use relative URLs for legacy API
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  });

  // Add request interceptor to include CSRF token
  axiosInstance.interceptors.request.use((config) => {
    const csrfToken = getCSRFToken();

    if (csrfToken) {
      config.headers = config.headers || {};
      config.headers['X-CSRF-Token'] = csrfToken;
    }

    return config;
  });

  // Handle the request and unwrap the response data
  return axiosInstance(config)
    .then((response: AxiosResponse<T>) => response.data)
    .catch((error: AxiosError) => {
      console.error('[legacyApiInstance] Error:', {
        url: config.url,
        method: config.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });

      // Transform error to match generated client pattern
      if (error.response?.data) {
        const errorData = error.response.data as any;
        if (errorData.error) {
          throw new Error(errorData.error);
        }
      }
      throw error;
    });
};

export type ErrorType<Error> = AxiosError<Error>;
